'use client'

import * as React from 'react'
import useEmblaCarousel, {type UseEmblaCarouselType} from 'embla-carousel-react'
import {useIntl} from 'react-intl'

import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'
import {ChevronLeftIcon, ChevronRightIcon} from 'lucide-react'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext],
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on('reInit', onSelect)
    api.on('select', onSelect)

    return () => {
      api?.off('reInit', onSelect)
      api?.off('select', onSelect)
    }
  }, [api, onSelect])

  React.useEffect(() => {
    if (!api) return

    const scheduleReInit = () => {
      requestAnimationFrame(() => {
        api.reInit()
      })
    }

    const viewport = api.containerNode()
    if (!viewport) return

    const resizeObserver = new ResizeObserver(() => {
      scheduleReInit()
    })
    resizeObserver.observe(viewport)

    // Embla moves the slides by writing `style` on the container and, when
    // looping, on each slide; embla plugins (ClassNames) write `class` there
    // too. Those writes are not a layout change. A reInit on them makes embla
    // write again, so the carousel reinitialised every frame and never
    // animated. Ignore attribute writes on embla's own nodes.
    const ownedByEmbla = (node: Node) =>
      node === api.containerNode() || api.slideNodes().includes(node as HTMLElement)
    const mutationObserver = new MutationObserver((mutations) => {
      const external = mutations.some(
        (mutation) => mutation.type !== 'attributes' || !ownedByEmbla(mutation.target),
      )
      if (external) scheduleReInit()
    })
    mutationObserver.observe(viewport, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true,
    })

    window.addEventListener('resize', scheduleReInit)
    window.addEventListener('orientationchange', scheduleReInit)

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', scheduleReInit)
      window.removeEventListener('orientationchange', scheduleReInit)
    }
  }, [api, carouselRef])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn('relative pointer-events-auto', className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({className, ...props}: React.ComponentProps<'div'>) {
  const {carouselRef, orientation} = useCarousel()
  // Defer attaching embla's callback ref until after the first commit so that
  // its DOM mutations (transforms, inline styles on slides, drag-handler
  // attributes) happen *after* hydration completes. Attaching during the
  // hydration commit caused intermittent React #418 mismatches on routes that
  // mount a carousel.
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])

  return (
    <div
      ref={mounted ? carouselRef : undefined}
      className="-mx-4 overflow-clip px-4 pb-3 pointer-events-auto touch-pan-y"
      data-slot="carousel-content"
    >
      <div
        className={cn('flex', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', className)}
        {...props}
      />
    </div>
  )
}

function CarouselItem({className, ...props}: React.ComponentProps<'div'>) {
  const {orientation} = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = 'ghost',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const {orientation, scrollPrev, canScrollPrev} = useCarousel()
  const intl = useIntl()
  const label = intl.formatMessage({id: 'carousel.previousSlide', defaultMessage: 'Previous slide'})

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        'absolute z-30 size-10 rounded-full pointer-events-auto touch-manipulation active:!-translate-y-1/2',
        orientation === 'horizontal'
          ? 'top-1/2 left-2 -translate-y-1/2 md:-left-12 md:left-auto'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      aria-label={label}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeftIcon className="size-6" />
      <span className="sr-only">{label}</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = 'ghost',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const {orientation, scrollNext, canScrollNext} = useCarousel()
  const intl = useIntl()
  const label = intl.formatMessage({id: 'carousel.nextSlide', defaultMessage: 'Next slide'})

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        'absolute z-30 size-10 rounded-full pointer-events-auto touch-manipulation active:!-translate-y-1/2',
        orientation === 'horizontal'
          ? 'top-1/2 right-2 -translate-y-1/2 md:-right-12'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      aria-label={label}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRightIcon className="size-6" />
      <span className="sr-only">{label}</span>
    </Button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel,
}
